/**
 * Execution Monitor Panel
 *
 * Real-time monitoring of workflow executions.
 * Shows execution list, step-by-step progress, and action results.
 */

import React, { useState } from 'react';
import {
  X,
  CheckCircle,
  XCircle,
  Clock,
  Loader2,
  ChevronRight,
  AlertTriangle,
  Activity,
  RefreshCw,
} from 'lucide-react';
import { useExecutions, useExecution, useExecutionEvents } from '../../hooks/useWorkflowBuilder';

const STATUS_CONFIG = {
  pending: { color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.1)', icon: Clock, label: 'Pending' },
  in_progress: { color: '#3b82f6', bg: 'rgba(59, 130, 246, 0.1)', icon: Loader2, label: 'Running' },
  completed: { color: '#10b981', bg: 'rgba(16, 185, 129, 0.1)', icon: CheckCircle, label: 'Completed' },
  failed: { color: '#ef4444', bg: 'rgba(239, 68, 68, 0.1)', icon: XCircle, label: 'Failed' },
  cancelled: { color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.1)', icon: XCircle, label: 'Cancelled' },
};

function StatusBadge({ status }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const Icon = config.icon;
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontWeight: 600,
        background: config.bg,
        color: config.color,
      }}
      data-testid={`execution-status-${status}`}
    >
      <Icon size={12} style={status === 'in_progress' ? { animation: 'spin 1s linear infinite' } : {}} />
      {config.label}
    </span>
  );
}

function TimeAgo({ date }) {
  if (!date) return null;
  const now = new Date();
  const then = new Date(date);
  const diff = Math.floor((now - then) / 1000);

  if (diff < 60) return <span>{diff}s ago</span>;
  if (diff < 3600) return <span>{Math.floor(diff / 60)}m ago</span>;
  if (diff < 86400) return <span>{Math.floor(diff / 3600)}h ago</span>;
  return <span>{then.toLocaleDateString()}</span>;
}

function AuditTrailSection({ events }) {
  const [showAll, setShowAll] = useState(false);
  const INITIAL_LIMIT = 10;

  if (!events?.length) return null;

  const displayedEvents = showAll ? events : events.slice(0, INITIAL_LIMIT);
  const hasMore = events.length > INITIAL_LIMIT;

  return (
    <div style={styles.section}>
      <div style={styles.sectionTitle}>
        Audit Trail ({events.length} events)
      </div>
      <div style={styles.eventsList}>
        {displayedEvents.map((event) => (
          <div key={event.id} style={styles.eventItem}>
            <div style={styles.eventLeft}>
              <span style={styles.eventType}>{formatEventType(event.event_type)}</span>
              {event.node_id && (
                <span style={styles.eventNode}>{event.node_id}</span>
              )}
            </div>
            <span style={styles.eventTime}>
              <TimeAgo date={event.created_at} />
            </span>
          </div>
        ))}
      </div>
      {hasMore && (
        <button
          onClick={() => setShowAll(!showAll)}
          style={styles.showMoreBtn}
        >
          {showAll ? 'Show Less' : `Show All ${events.length} Events`}
        </button>
      )}
    </div>
  );
}

function formatEventType(type) {
  const labels = {
    execution_started: 'Started',
    execution_completed: 'Completed',
    execution_failed: 'Failed',
    node_entered: 'Node Entered',
    node_completed: 'Node Done',
    action_started: 'Action Started',
    action_completed: 'Action Done',
    action_failed: 'Action Failed',
    condition_evaluated: 'Condition Checked',
  };
  return labels[type] || type;
}

function ExecutionDetail({ executionId, onClose }) {
  const { data: execution, isLoading } = useExecution(executionId);
  const { data: events } = useExecutionEvents(executionId);

  if (isLoading) {
    return (
      <div style={styles.detailLoading}>
        <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
        <span>Loading execution...</span>
      </div>
    );
  }

  if (!execution) return null;

  return (
    <div style={styles.detail} data-testid="execution-detail">
      <div style={styles.detailHeader}>
        <div>
          <div style={styles.detailTitle}>
            {execution.workflow_name || 'Workflow Execution'}
          </div>
          <div style={styles.detailMeta}>
            <StatusBadge status={execution.status} />
            <span style={{ fontSize: '12px', color: 'var(--text-secondary)' }}>
              {execution.execution_mode}
            </span>
          </div>
        </div>
        <button onClick={onClose} style={styles.closeBtn}>
          <X size={16} />
        </button>
      </div>

      {/* Trigger Info */}
      {execution.trigger_event && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Trigger</div>
          <div style={styles.triggerInfo}>
            <span style={styles.triggerType}>{execution.trigger_event.type}</span>
            <TimeAgo date={execution.trigger_event.timestamp} />
          </div>
        </div>
      )}

      {/* Step History */}
      {execution.step_history?.length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Execution Steps</div>
          <div style={styles.timeline}>
            {execution.step_history.map((step, i) => (
              <div key={i} style={styles.timelineItem}>
                <div style={styles.timelineDot(step.status === 'completed' ? '#10b981' : step.status === 'failed' ? '#ef4444' : '#3b82f6')} />
                <div style={styles.timelineContent}>
                  <div style={styles.stepName}>{step.node_id || `Step ${i + 1}`}</div>
                  <div style={styles.stepMeta}>
                    {step.action && <span>{step.action}</span>}
                    {step.duration_ms && <span>{step.duration_ms}ms</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Results */}
      {Object.keys(execution.action_results || {}).length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Action Results</div>
          {Object.entries(execution.action_results).map(([nodeId, result]) => (
            <div key={nodeId} style={styles.resultItem}>
              <div style={styles.resultNode}>{nodeId}</div>
              <pre style={styles.resultData}>
                {JSON.stringify(result, null, 2)}
              </pre>
            </div>
          ))}
        </div>
      )}

      {/* Error Log */}
      {execution.error_log?.length > 0 && (
        <div style={styles.section}>
          <div style={{ ...styles.sectionTitle, color: '#ef4444' }}>Errors</div>
          {execution.error_log.map((err, i) => (
            <div key={i} style={styles.errorItem}>
              <AlertTriangle size={14} style={{ color: '#ef4444', flexShrink: 0 }} />
              <div>
                <div style={styles.errorNode}>{err.node_id || 'Unknown'}</div>
                <div style={styles.errorMessage}>{err.error || err.message}</div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Compliance Explanation */}
      {execution.action_results && Object.keys(execution.action_results).length > 0 && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Compliance Decisions</div>
          <div style={styles.complianceExplanation}>
            {Object.entries(execution.action_results).map(([nodeId, result]) => {
              if (result.action_id === 'run_screening' && result.data) {
                return (
                  <div key={nodeId} style={styles.complianceItem}>
                    <div style={styles.complianceHeader}>
                      <span style={styles.complianceAction}>Sanctions Screening</span>
                      <span style={{
                        ...styles.complianceStatus,
                        background: result.data.has_hits ? 'rgba(239, 68, 68, 0.1)' : 'rgba(16, 185, 129, 0.1)',
                        color: result.data.has_hits ? '#ef4444' : '#10b981',
                      }}>
                        {result.data.has_hits ? `${result.data.hit_count} Hit(s)` : 'Clear'}
                      </span>
                    </div>
                    <div style={styles.complianceReason}>
                      {result.data.has_hits
                        ? `Potential match found against sanctions lists. Manual review required per OFAC/AML regulations.`
                        : `No matches found. Applicant cleared for sanctions compliance.`}
                    </div>
                  </div>
                );
              }
              if (result.action_id === 'update_status') {
                return (
                  <div key={nodeId} style={styles.complianceItem}>
                    <div style={styles.complianceHeader}>
                      <span style={styles.complianceAction}>Status Decision</span>
                      <span style={{
                        ...styles.complianceStatus,
                        background: result.data.new_status === 'approved' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(245, 158, 11, 0.1)',
                        color: result.data.new_status === 'approved' ? '#10b981' : '#f59e0b',
                      }}>
                        {result.data.new_status}
                      </span>
                    </div>
                    <div style={styles.complianceReason}>
                      {result.data.new_status === 'approved'
                        ? `All verification steps passed. Applicant meets KYC/AML requirements.`
                        : `${result.data.new_status === 'review' ? 'Manual review required.' : 'Decision pending.'} Reason: ${result.data.reason || 'Workflow logic'}`}
                    </div>
                  </div>
                );
              }
              return null;
            })}
          </div>
        </div>
      )}

      {/* Events Timeline with Pagination */}
      <AuditTrailSection events={events} />

      {/* Final Outcome */}
      {execution.final_outcome && (
        <div style={styles.section}>
          <div style={styles.sectionTitle}>Outcome</div>
          <div style={{
            padding: '8px 12px',
            borderRadius: '6px',
            background: execution.final_outcome === 'approved' ? 'rgba(16, 185, 129, 0.1)' : 'rgba(239, 68, 68, 0.1)',
            color: execution.final_outcome === 'approved' ? '#10b981' : '#ef4444',
            fontWeight: 600,
            fontSize: '13px',
          }}>
            {execution.final_outcome}
          </div>
        </div>
      )}
    </div>
  );
}

export default function ExecutionMonitor({ workflowId, onClose }) {
  const [statusFilter, setStatusFilter] = useState(null);
  const [selectedExecution, setSelectedExecution] = useState(null);

  const filters = { limit: 50 };
  if (workflowId) filters.workflow_id = workflowId;
  if (statusFilter) filters.status = statusFilter;

  const { data, isLoading, refetch } = useExecutions(filters);
  const executions = data?.items || [];

  const statusCounts = executions.reduce((acc, ex) => {
    acc[ex.status] = (acc[ex.status] || 0) + 1;
    return acc;
  }, {});

  if (selectedExecution) {
    return (
      <div style={styles.container} data-testid="execution-monitor">
        <ExecutionDetail
          executionId={selectedExecution}
          onClose={() => setSelectedExecution(null)}
        />
      </div>
    );
  }

  return (
    <div style={styles.container} data-testid="execution-monitor">
      <div style={styles.header}>
        <div style={styles.headerTitle}>
          <Activity size={18} style={{ color: 'var(--accent-primary)' }} />
          <span>Executions</span>
        </div>
        <div style={styles.headerActions}>
          <button onClick={() => refetch()} style={styles.refreshBtn} title="Refresh">
            <RefreshCw size={14} />
          </button>
          <button onClick={onClose} style={styles.closeBtn}>
            <X size={16} />
          </button>
        </div>
      </div>

      {/* Status Filters */}
      <div style={styles.filters}>
        <button
          onClick={() => setStatusFilter(null)}
          style={statusFilter === null ? styles.filterActive : styles.filterBtn}
        >
          All ({data?.total || 0})
        </button>
        {Object.entries(STATUS_CONFIG).map(([key, config]) => (
          <button
            key={key}
            onClick={() => setStatusFilter(key)}
            style={statusFilter === key ? { ...styles.filterActive, color: config.color } : styles.filterBtn}
          >
            {config.label} ({statusCounts[key] || 0})
          </button>
        ))}
      </div>

      {/* Execution List */}
      <div style={styles.list}>
        {isLoading ? (
          <div style={styles.loading}>
            <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            <span>Loading executions...</span>
          </div>
        ) : executions.length === 0 ? (
          <div style={styles.empty}>
            <Activity size={32} style={{ opacity: 0.3 }} />
            <span>No executions yet</span>
            <span style={{ fontSize: '12px', color: 'var(--text-tertiary)' }}>
              Publish a workflow and trigger it to see executions here
            </span>
          </div>
        ) : (
          executions.map((ex) => (
            <div
              key={ex.id}
              style={styles.executionItem}
              onClick={() => setSelectedExecution(ex.id)}
              data-testid={`execution-item-${ex.id}`}
            >
              <div style={styles.executionMain}>
                <div style={styles.executionName}>
                  {ex.workflow_name || 'Unnamed Workflow'}
                </div>
                <StatusBadge status={ex.status} />
              </div>
              <div style={styles.executionMeta}>
                <span>{ex.execution_mode}</span>
                {ex.final_outcome && (
                  <span style={{ fontWeight: 600 }}>{ex.final_outcome}</span>
                )}
                <TimeAgo date={ex.created_at} />
              </div>
              <ChevronRight size={14} style={{ color: 'var(--text-tertiary)', flexShrink: 0 }} />
            </div>
          ))
        )}
      </div>
    </div>
  );
}

const styles = {
  container: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    background: 'var(--bg-primary)',
    borderLeft: '1px solid var(--border-color)',
    width: '380px',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)',
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontWeight: 600,
    fontSize: '14px',
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: 'none',
    background: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  closeBtn: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    border: 'none',
    background: 'transparent',
    borderRadius: '4px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
  },
  filters: {
    display: 'flex',
    gap: '4px',
    padding: '8px 12px',
    borderBottom: '1px solid var(--border-color)',
    overflowX: 'auto',
    flexShrink: 0,
  },
  filterBtn: {
    padding: '4px 8px',
    border: '1px solid var(--border-color)',
    background: 'transparent',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    color: 'var(--text-secondary)',
    whiteSpace: 'nowrap',
  },
  filterActive: {
    padding: '4px 8px',
    border: '1px solid var(--accent-primary)',
    background: 'var(--accent-glow-medium)',
    borderRadius: '4px',
    fontSize: '11px',
    cursor: 'pointer',
    color: 'var(--accent-primary)',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  list: {
    flex: 1,
    overflowY: 'auto',
    padding: '8px',
  },
  loading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '40px 20px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
  },
  empty: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '40px 20px',
    color: 'var(--text-secondary)',
    fontSize: '13px',
    textAlign: 'center',
  },
  executionItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    padding: '10px 12px',
    borderRadius: '6px',
    cursor: 'pointer',
    border: '1px solid var(--border-color)',
    marginBottom: '6px',
    transition: 'background 0.15s',
  },
  executionMain: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  executionName: {
    fontSize: '13px',
    fontWeight: 500,
    color: 'var(--text-primary)',
  },
  executionMeta: {
    display: 'flex',
    gap: '8px',
    fontSize: '11px',
    color: 'var(--text-tertiary)',
  },
  // Detail view styles
  detail: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    overflowY: 'auto',
  },
  detailLoading: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '40px',
    color: 'var(--text-secondary)',
  },
  detailHeader: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '16px',
    borderBottom: '1px solid var(--border-color)',
    background: 'var(--bg-secondary)',
  },
  detailTitle: {
    fontSize: '15px',
    fontWeight: 600,
    marginBottom: '6px',
  },
  detailMeta: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  section: {
    padding: '12px 16px',
    borderBottom: '1px solid var(--border-color)',
  },
  sectionTitle: {
    fontSize: '11px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    color: 'var(--text-secondary)',
    marginBottom: '8px',
  },
  triggerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '13px',
  },
  triggerType: {
    padding: '2px 8px',
    background: 'var(--bg-tertiary)',
    borderRadius: '4px',
    fontSize: '12px',
    fontFamily: 'monospace',
  },
  timeline: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0',
    paddingLeft: '8px',
  },
  timelineItem: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    paddingBottom: '12px',
    borderLeft: '2px solid var(--border-color)',
    paddingLeft: '12px',
    marginLeft: '4px',
  },
  timelineDot: (color) => ({
    width: '10px',
    height: '10px',
    borderRadius: '50%',
    background: color,
    flexShrink: 0,
    marginTop: '3px',
    marginLeft: '-18px',
  }),
  timelineContent: {
    flex: 1,
  },
  stepName: {
    fontSize: '13px',
    fontWeight: 500,
  },
  stepMeta: {
    display: 'flex',
    gap: '8px',
    fontSize: '11px',
    color: 'var(--text-tertiary)',
    marginTop: '2px',
  },
  resultItem: {
    marginBottom: '8px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    overflow: 'hidden',
  },
  resultNode: {
    padding: '6px 10px',
    background: 'var(--bg-tertiary)',
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  resultData: {
    padding: '8px 10px',
    margin: 0,
    fontSize: '11px',
    fontFamily: 'monospace',
    maxHeight: '120px',
    overflowY: 'auto',
    background: 'var(--bg-primary)',
    color: 'var(--text-secondary)',
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
  },
  errorItem: {
    display: 'flex',
    gap: '8px',
    padding: '8px',
    background: 'rgba(239, 68, 68, 0.05)',
    borderRadius: '6px',
    marginBottom: '6px',
  },
  errorNode: {
    fontSize: '12px',
    fontWeight: 600,
    fontFamily: 'monospace',
  },
  errorMessage: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    marginTop: '2px',
  },
  eventsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  eventItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '4px',
    background: 'var(--bg-secondary)',
  },
  eventType: {
    fontSize: '11px',
    fontFamily: 'monospace',
    color: 'var(--text-primary)',
  },
  eventTime: {
    fontSize: '11px',
    color: 'var(--text-tertiary)',
  },
  eventLeft: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  eventNode: {
    fontSize: '10px',
    fontFamily: 'monospace',
    color: 'var(--text-tertiary)',
    padding: '1px 4px',
    background: 'var(--bg-tertiary)',
    borderRadius: '3px',
  },
  showMoreBtn: {
    width: '100%',
    padding: '8px',
    marginTop: '8px',
    border: '1px dashed var(--border-color)',
    background: 'transparent',
    borderRadius: '6px',
    fontSize: '12px',
    color: 'var(--text-secondary)',
    cursor: 'pointer',
    transition: 'all 0.15s',
  },
  complianceExplanation: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  complianceItem: {
    padding: '10px',
    border: '1px solid var(--border-color)',
    borderRadius: '6px',
    background: 'var(--bg-secondary)',
  },
  complianceHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '6px',
  },
  complianceAction: {
    fontSize: '12px',
    fontWeight: 600,
    color: 'var(--text-primary)',
  },
  complianceStatus: {
    fontSize: '11px',
    fontWeight: 600,
    padding: '2px 8px',
    borderRadius: '10px',
  },
  complianceReason: {
    fontSize: '12px',
    color: 'var(--text-secondary)',
    lineHeight: 1.4,
  },
};
